import React, { useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Radar } from "lucide-react";
import { useLang } from "@/i18n";
import { Overline } from "@/ui";
import { usePageEvent } from "@/lib/analytics";

export default function RequestReceived() {
  const { L, lang } = useLang();
  const nav = useNavigate();
  const location = useLocation();
  const trackerToken = location.state?.trackerToken || null;

  // Lead completed conversion — fires once per real load of /solicitud-recibida.
  usePageEvent({ event: "generate_lead", lead_type: "quote_request", business: "dukk" });

  useEffect(() => {
    document.documentElement.lang = lang === "en" ? "en" : "es-MX";
    document.title = L("Request received | dukk", "Solicitud recibida | dukk");
    let robots = document.head.querySelector('meta[name="robots"]');
    if (!robots) { robots = document.createElement("meta"); robots.setAttribute("name", "robots"); document.head.appendChild(robots); }
    robots.setAttribute("content", "noindex, follow");
    return () => { if (robots) robots.setAttribute("content", "index, follow"); };
  }, [L, lang]);

  return (
    <motion.div
      data-testid="request-received-page"
      initial={{ opacity: 0, y: 24, filter: "blur(10px)" }}
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      transition={{ duration: 0.7 }}
      className="grain min-h-screen pt-32 pb-24"
    >
      <div className="max-w-xl mx-auto px-6 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 160 }}
          className="mx-auto h-16 w-16 rounded-full border border-[#C7A668] flex items-center justify-center shadow-[0_0_24px_rgba(199,166,104,0.35)]"
        >
          <Check className="text-[#C7A668]" size={30} />
        </motion.div>

        <h1 data-testid="request-received-title" className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-8 text-white">
          {L("Request received.", "Solicitud recibida.")}
        </h1>
        <p className="text-[#C7A668] font-mono text-sm tracking-widest uppercase mt-3">
          {L("Concierge validation pending", "Validación de concierge pendiente")}
        </p>

        <p className="text-white/55 font-light mt-6 leading-relaxed">
          {L(
            "Thank you for contacting dukk. Our team will review the aircraft details, location, access and service scope before confirming availability and the final quote.",
            "Gracias por contactar a dukk. Nuestro equipo revisará la información de la aeronave, ubicación, acceso y alcance del servicio antes de confirmar disponibilidad y cotización final."
          )}
        </p>

        <div className="mt-7 rounded-sm border border-white/10 bg-white/[0.03] p-6">
          <Overline gold>{L("Important", "Importante")}</Overline>
          <p className="text-white/70 font-light mt-3 leading-relaxed text-sm">
            {L(
              "This does not yet confirm a service. A dukk concierge will contact you to validate the details.",
              "Esto aún no confirma un servicio. Un concierge de dukk se pondrá en contacto contigo para validar los detalles."
            )}
          </p>
        </div>

        <div className="mt-9 flex flex-col items-center gap-4">
          {trackerToken && (
            <Link
              to={`/servicio/${trackerToken}`}
              data-testid="request-received-track-btn"
              className="inline-flex items-center gap-2 bg-[#C7A668] text-[#050505] px-7 py-3.5 font-semibold tracking-widest uppercase text-sm hover:opacity-90 transition-all no-underline"
              style={{ borderRadius: 999 }}
            >
              <Radar size={16} /> {L("Track my service", "Ver seguimiento de mi servicio")}
            </Link>
          )}
          <button
            data-testid="request-received-home-btn"
            onClick={() => nav(lang === "en" ? "/en" : "/")}
            className={`px-7 py-3.5 font-semibold tracking-widest uppercase text-sm hover:opacity-90 transition-all ${trackerToken ? "text-white/60 border border-white/15" : "bg-[#C7A668] text-[#050505]"}`}
            style={{ borderRadius: 999 }}
          >
            {L("Back to home", "Volver al inicio")}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
